/*
    * Created on Wed Oct 11 2023 4:46:23 PM
    *
    * ***************************************************************************************
    * Copyright (c) AVPS Technologies Inc 2023 
    *
    * All Rights Reserved
    * Unpublished rights are reserved under the copyright laws of the United States.
    * The software contained in this media is proprietary to and embodies confidential
    * the technology of AVPS Technologies Inc. Possession, use, duplication or
    * dissemination of the software and media is authorized only pursuant to a valid
    * written license from AVPS Technologies Inc.
    *
    * The above copyright notice and this permission notice shall be included in
    * all copies or substantial portions of the Software.
    *
    * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
    * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
    * 252.227-7013 or in FAR 52.227-19, as applicable.
    ****************************************************************************************
 */
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import ProcessingProgress from './ProcessingProgress';
import { useSelector } from 'react-redux';
import {
  formatCurrency as formatSharedCurrency,
  formatNumber as formatSharedNumber
} from '../../../../../utils/format';

const formatCurrency = (amount, companyState) => formatSharedCurrency(amount, companyState);

const formatNumber = (number, companyState) => formatSharedNumber(number, companyState);

const BookingSummary = ({
  totalPrice,
  duration,
  pricePerHour,
  totalOccurrences,
  hasRecurrence,
  isProcessing,
  isCheckingAvailability = false,   // ← new: true while recurrence slots are being fetched
  processingProgress,
  onConfirm,
  onLoginRequired,
  availabilityStatus,
  onViewSchedule
}) => {
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const companyState = useSelector((state) => state?.companyReducer?.company);

  const formattedTotalPrice    = formatCurrency(totalPrice, companyState);
  const formattedPricePerHour  = formatCurrency(pricePerHour, companyState);
  const formattedOccurrences   = formatNumber(totalOccurrences, companyState);

  const handleConfirmClick = () => {
    if (!isAuthenticated) {
      onLoginRequired();
      return;
    }
    onConfirm();
  };

  // ── Derive the correct button label ─────────────────────────────────────────
  const renderButtonContent = () => {
    if (isCheckingAvailability) {
      return (
        <>
          <Loader2 className="animate-spin w-4 h-4 mr-2" />
          Checking availability…
        </>
      );
    }

    if (isProcessing) {
      return (
        <>
          <Loader2 className="animate-spin w-4 h-4 mr-2" />
          Processing your booking…
        </>
      );
    }

    if (!isAuthenticated) {
      return (
        <>
          Login to Confirm Booking
          <ArrowRight className="w-4 h-4 ml-2" />
        </>
      );
    }

    return (
      <>
        Confirm Booking
        <ArrowRight className="w-4 h-4 ml-2" />
      </>
    );
  };
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="bg-slate-50 p-5 rounded-xl border border-slate-200"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-600 font-medium text-sm">Total Cost</span>
        <span className="text-2xl font-bold text-slate-900">
          {formattedTotalPrice}
        </span>
      </div>

      <div className="text-xs text-slate-500 text-center py-2 bg-white rounded-lg border border-slate-200">
        {duration} {duration === 1 ? 'hour' : 'hours'} × {formattedPricePerHour}/hour
        {hasRecurrence && (
          <span className="text-emerald-600 font-semibold ml-1">
            × {formattedOccurrences} {totalOccurrences === 1 ? 'occurrence' : 'occurrences'}
          </span>
        )}
      </div>

      {/* Availability Status */}
      {availabilityStatus && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded-lg border-2 mt-3 ${
            availabilityStatus.type === 'success'
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-start">
              <div className={`w-2 h-2 rounded-full mt-1.5 mr-2 ${
                availabilityStatus.type === 'success' ? 'bg-emerald-500' : 'bg-amber-500'
              }`} />
              <span className={`text-xs font-medium ${
                availabilityStatus.type === 'success' ? 'text-emerald-700' : 'text-amber-700'
              }`}>
                {availabilityStatus.message}
              </span>
            </div>
            {availabilityStatus.type === 'warning' && onViewSchedule && (
              <button
                onClick={onViewSchedule}
                className="text-xs font-medium text-amber-600 hover:text-amber-700 underline"
              >
                View
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Progress bar — only shown during actual booking submission, not availability check */}
      {isProcessing && !isCheckingAvailability && processingProgress.total > 0 && (
        <ProcessingProgress
          current={processingProgress.current}
          total={processingProgress.total}
        />
      )}

      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={handleConfirmClick}
        disabled={isProcessing}
        className="w-full bg-slate-900 text-white py-3.5 font-semibold rounded-lg mt-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center hover:bg-slate-800"
      >
        {renderButtonContent()}
      </motion.button>
    </motion.div>
  );
};

export default BookingSummary;
